
import React, { useState, useMemo } from 'react';
import { Language, Plan } from '../types';
import PlansHeader from './Plans/PlansHeader';
import PlansScanner from './Plans/PlansScanner';
import PlansActivation from './Plans/PlansActivation';
import PlansList from './Plans/PlansList';
import PlansFooter from './Plans/PlansFooter';

interface PlansProps {
  isOpen: boolean;
  onClose: () => void;
  onActivationSuccess: (planType: 'pro' | 'enterprise') => void;
  language: Language;
  plans: Plan[];
}

const Plans: React.FC<PlansProps> = ({ isOpen, onClose, onActivationSuccess, language, plans }) => {
  const [promoCode, setPromoCode] = useState('');
  const [isActivating, setIsActivating] = useState(false);
  const [activationStatus, setActivationStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [expandedPlan, setExpandedPlan] = useState<string | null>('pro'); 
  const [isScanning, setIsScanning] = useState(false);

  const isRtl = language === 'ar';

  const visiblePlans = useMemo(() => plans.filter(p => !p.hidden), [plans]);

  if (!isOpen) return null;

  const handleActivateCode = () => {
    if (!promoCode.trim()) return;
    setIsActivating(true);
    
    const code = promoCode.trim();

    setTimeout(() => {
      setIsActivating(false);
      
      if (code === 'LAHJA-Admin') {
        setActivationStatus('success');
        setTimeout(() => onActivationSuccess('enterprise'), 1000);
      } else if (code.toUpperCase() === 'LAHJA2025') {
        setActivationStatus('success');
        setTimeout(() => onActivationSuccess('pro'), 1000);
      } else {
        setActivationStatus('error');
        setTimeout(() => setActivationStatus('idle'), 3000);
      }
    }, 1500);
  };

  const handleScanSuccess = (decodedText: string) => {
    setPromoCode(decodedText);
    setIsScanning(false);
  };

  return (
    <div className={`fixed inset-0 z-[110] flex items-center justify-center p-4 overflow-hidden ${isRtl ? 'rtl' : 'ltr'}`}>
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300" onClick={onClose} />
      
      <PlansScanner 
        isScanning={isScanning} 
        onScanSuccess={handleScanSuccess} 
        onClose={() => setIsScanning(false)} 
      />

      <div className="relative w-full max-w-4xl bg-slate-50 dark:bg-[#0f0f13] rounded-[32px] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[85vh] border border-slate-200 dark:border-white/10">
        <PlansHeader language={language} onClose={onClose} />

        <div className="flex-1 overflow-y-auto custom-scrollbar p-4 lg:p-8">
          <PlansActivation 
            language={language}
            promoCode={promoCode}
            setPromoCode={setPromoCode}
            isActivating={isActivating}
            activationStatus={activationStatus}
            onActivate={handleActivateCode}
            onStartScan={() => setIsScanning(true)}
          />
          <PlansList 
            language={language}
            plans={visiblePlans}
            expandedPlan={expandedPlan}
            setExpandedPlan={setExpandedPlan}
          />
        </div>
        
        <PlansFooter language={language} />
      </div>
    </div>
  );
};

export default Plans;
